import { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useReactTable, getCoreRowModel } from "@tanstack/react-table";
import { handleFilterChange } from "../utils/TableFilterService";

const useTableConfiguration = ({
  tableConfig,
  formatData,
  tableId = "base-table",
  initialColumnOrder = [],
  data,
  recruits,
  formattedData,
  contextState,
  contextActions,
  startEditingRow,
}) => {
  const {
    columnVisibility,
    currentPage,
    rowsPerPage,
    sorting,
    filters,
    pinnedColumns,
    columnSizing,
    columnOrder,
    lowerValue,
    upperValue,
  } = contextState;

  const {
    setColumnVisibility,
    setSorting,
    setFilters,
    setColumnsVisible,
    togglePinnedColumn,
    setColumnSizing,
    setTempColumnSizing,
    toggleColumnWidth,
    updateColumnCondition,
    setColumnOrder,
    setUpperValue,
    setLowerValue,
  } = contextActions;


  // Refs for initialization tracking
  const isUpdatingFromContext = useRef(false);
  const prevColumnSizingRef = useRef({});
  const columnSizingInitialized = useRef(false);
  const columnOrderInitialized = useRef(false);
  const isResizing = useRef(false);

  // Check if this is an analytics table
  const isAnalyticsTable = tableId === "analytics-table";

  // Sync from context only when it changes and we're not actively resizing
  useEffect(() => {
    if (isResizing.current) return;

    const stringifiedPrev = JSON.stringify(prevColumnSizingRef.current);
    const stringifiedCurrent = JSON.stringify(columnSizing);

    if (stringifiedPrev !== stringifiedCurrent) {
      isUpdatingFromContext.current = true;
      setColumnSizing(columnSizing);
      setTempColumnSizing(columnSizing);
      prevColumnSizingRef.current = columnSizing;
      isUpdatingFromContext.current = false;
    }
  }, [columnSizing, contextActions]);

  // Initialize column sizing if not already set
  useEffect(() => {
    if (
      !isUpdatingFromContext.current &&
      !isResizing.current &&
      !columnSizingInitialized.current &&
      Object.keys(columnSizing).length === 0 &&
      tableConfig.columns &&
      tableConfig.columns.length > 0
    ) {
      const initialSizing = {};
      const defaultWidth = 150;

      tableConfig.columns.forEach((column) => {
        initialSizing[column.id] = column.size || defaultWidth;
      });

      columnSizingInitialized.current = true;
      setColumnSizing(initialSizing);
      setTempColumnSizing(initialSizing);

      setTimeout(() => {
        if (!isUpdatingFromContext.current) {
          setColumnSizing(initialSizing);
        }
      }, 0);
    }
  }, [tableConfig.columns, contextActions, setColumnSizing, columnSizing]);

  // Initialize column order safely
  useEffect(() => {
    if (
      tableConfig.columns &&
      tableConfig.columns.length > 0 &&
      columnOrder.length === 0 &&
      !columnOrderInitialized.current
    ) {
      columnOrderInitialized.current = true;
      setColumnOrder(tableConfig.columns.map((col) => col.id));
    }
  }, [tableConfig.columns, columnOrder.length, setColumnOrder]);

  // Set up effective column order
  const effectiveColumnOrder = useMemo(() => {
    return columnOrder.length > 0
      ? columnOrder
      : initialColumnOrder.length > 0
      ? initialColumnOrder
      : tableConfig.columns?.map((col) => col.id) || [];
  }, [columnOrder, initialColumnOrder, tableConfig.columns]);

  // Helper function to check if a column is pinned
  const isColumnPinned = useCallback(
    (columnId) => {
      return pinnedColumns.includes(columnId);
    },
    [pinnedColumns]
  );

  // Get style for pinned columns
  const getPinnedColumnStyle = useCallback(
    (columnId) => {
      if (!isColumnPinned(columnId)) return {};

      let left = 0;
      for (const pinnedId of pinnedColumns) {
        if (pinnedId === columnId) break;
        left += columnSizing[pinnedId] || 150;
      }

      return {
        left: `${left}px`,
        backgroundColor: "white",
        zIndex: 30,
        boxShadow: "20px 20px 20px #16274633",
      };
    },
    [columnSizing, isColumnPinned, pinnedColumns]
  );

  // Calculate total pinned width
  const totalPinnedWidth = useMemo(() => {
    let total = 0;
    pinnedColumns.forEach((columnId) => {
      total += columnSizing[columnId] || 150;
    });
    return total;
  }, [pinnedColumns, columnSizing]);

  // Check if a column has active filters or sorting
  const hasActiveFiltersOrSorting = useCallback(
    (columnId) => {
      const hasActiveFilter =
        filters && filters[columnId] && filters[columnId].length > 0;
      const hasActiveSorting =
        sorting && sorting.some((sort) => sort.id === columnId);
      return hasActiveFilter || hasActiveSorting;
    },
    [filters, sorting]
  );

  // Define predefined columns to toggle
  const getPredefinedColumns = useCallback(() => {
    return [
      "date_of_interview",
      "date_of_translate",
      "date_of_offer",
      "date_of_fired",
      "commentary",
      "working_phone",
      "phone_number",
      "email",
      "NDA_date",
      "FB_IG",
      "linkedin",
    ];
  }, []);


  // Handler for toggling predefined columns
  const handleTogglePredefinedColumns = useCallback(
    (newState) => {
      const predefinedCols = getPredefinedColumns();
      const newVisibility = { ...columnVisibility };

      predefinedCols.forEach((colId) => {
        newVisibility[colId] = newState;
      });

      setColumnVisibility(newVisibility);
      setColumnsVisible(newState);
    },
    [
      columnVisibility,
      setColumnVisibility,
      setColumnsVisible,
      getPredefinedColumns,
    ]
  );

  const table = useReactTable({
    data: formattedData,
    columns: tableConfig.columns || [],
    getCoreRowModel: getCoreRowModel(),

    // Column resizing
    columnResizeMode: "onChange",
    enableColumnResizing: true,

    // State
    state: {
      columnVisibility,
      columnSizing: columnSizing,
      pagination: {
        pageIndex: currentPage - 1,
        pageSize: rowsPerPage,
      },
      columnOrder: effectiveColumnOrder,
    },

    // Manual control
    manualSorting: true,
    manualPagination: true,
    pageCount: data?.Pagination?.totalPages || 0,
    onColumnOrderChange: setColumnOrder,

    // Custom options for use in cells/headers
    lowerValue: lowerValue,
    upperValue: upperValue,
    setUpperValue: setUpperValue,
    setLowerValue: setLowerValue,
    startEditingRow: startEditingRow,
    state: contextState,

    updateColumnCondition,
    toggleColumnWidth,
    setColumnVisibility,
    togglePinnedColumn,
  });


  const applySortToContext = useCallback(
    (sortObject) => {
      if (!sortObject || !sortObject.id) return;
      setSorting([sortObject]);
    },
    [setSorting]
  );

  const hideColumnInContext = useCallback(
    (columnId) => {
      if (setColumnVisibility) {
        setColumnVisibility((prev) => ({ ...prev, [columnId]: false }));
      }
    },
    [setColumnVisibility]
  );

  const pinColumnInContext = useCallback(
    (columnId, pinState) => {
      if (togglePinnedColumn) {
        togglePinnedColumn(columnId, pinState);
      }
    },
    [togglePinnedColumn]
  );

  return {
    // Configuration state
    isAnalyticsTable,
    effectiveColumnOrder,
    totalPinnedWidth,

    // Table instance
    table,

    // Helper functions
    isColumnPinned,
    getPinnedColumnStyle,
    hasActiveFiltersOrSorting,
    getPredefinedColumns,

    // Event handlers
    handleTogglePredefinedColumns,
    handleFilterChange,
    applySortToContext,
    hideColumnInContext,
    pinColumnInContext,

    // Refs for external use
    isUpdatingFromContext,
    isResizing,
  };
};

export default useTableConfiguration;
